import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtom } from 'jotai';
import { eSensorChartAtom, DeviceEUIProps, sensorInfoAtom } from '../atoms-types/eSensors';
import moment, { Moment } from 'moment-timezone';
import { buildChartTimeAxis } from '../converters/chartTimeAxis';
import { FRIDGE_ALARM_F, FRIDGE_WARNING_F } from '../hvac/hvacStatus';

interface SensorGraphProps extends DeviceEUIProps {
  sensorColor?: string;
  startDateTime: Moment;
  endDateTime: Moment;
  height?: number | string;
}

const SensorGraph: React.FC<SensorGraphProps> = ({ deviceEUI, Display, sensorColor, startDateTime, endDateTime, height = 400 }) => {
  const [esensorChart] = useAtom(eSensorChartAtom);
  const [sensorInfo] = useAtom(sensorInfoAtom);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  // Get current sensor data for real-time updates
  const getCurrentSensorData = () => {
    if (!deviceEUI) return null;
    
    const currentSensorInfo = sensorInfo[deviceEUI];
    if (!currentSensorInfo || currentSensorInfo.length === 0) return null;
    
    const sensor = currentSensorInfo[0];
    if (!sensor.jInfo) return null;
    
    const timestamp = (sensor.jInfo as any).Timestamp || new Date().toISOString();
    const jInfoObj = sensor.jInfo as Record<string, any>;
    
    // Create an object with all matching display values
    const displayValues = Array.isArray(Display) 
      ? Display.reduce((acc, key) => {
          const value = jInfoObj[key] ?? null;
          acc[key] = typeof value === 'number' ? value : 
                    (typeof value === 'string' && !isNaN(Number(value))) ? Number(value) : null;
          return acc;
        }, {} as Record<string, number | null>)
      : { [Display]: typeof jInfoObj[Display] === 'number' ? jInfoObj[Display] :
                 (typeof jInfoObj[Display] === 'string' && !isNaN(Number(jInfoObj[Display]))) ? 
                 Number(jInfoObj[Display]) : null };

    return {
      timestamp,
      ...displayValues
    };
  };

  // Combine historical data with current real-time data
  const getCombinedChartData = () => {
    const currentData = getCurrentSensorData();
    if (!currentData) return esensorChart;
    
    // Check if current data already exists in historical data
    const exists = esensorChart.some(item => item.timestamp === currentData.timestamp);
    if (exists) return esensorChart;
    
    // Add current data to the end
    return [...esensorChart, currentData as any];
  };

  const chartData = getCombinedChartData();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      const chartElement = document.querySelector('.echarts-for-react');
      if (chartElement) {
        const echartsInstance = (chartElement as any).__ECharts__;
        if (echartsInstance) {
          echartsInstance.resize();
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const chartTimestamps = chartData.map((item) => item.timestamp);
  const {
    labels: timestamps,
    verticalLines,
    shouldShowLabel,
    formatLabel,
  } = buildChartTimeAxis(chartTimestamps, startDateTime, endDateTime);

  let minRiverHeight = 220;
  chartData.forEach((item) => {
    if ((item as any).Height !== null && (item as any).Height !== undefined) {
      minRiverHeight = Math.min(minRiverHeight, Number((item as any).Height));
    }
  });

  const colors = ['orange','seagreen', 'steelblue', 'slateblue', 'firebrick', 'purple', 'crimson', 'gold'];
  
  // Define RiverHeight thresholds
  const riverHeightThresholds = {
    overDam: 217.74,
    overPathDam: 215.56,
    overPathDam2: 212.36,
    overPath11: 211.28
  };

  const currentSensor = deviceEUI ? sensorInfo[deviceEUI]?.[0] : undefined;
  const isFridgeTemp = currentSensor?.Type === 'FridgeTemp';
  // Fridge/HVAC threshold lines (°F). Resolve from Class thresholds when
  // present; otherwise fall back to legacy defaults.
  const fridgeTempThresholds = {
    warning:
      typeof currentSensor?.warningF === 'number' && Number.isFinite(currentSensor.warningF)
        ? currentSensor.warningF
        : FRIDGE_WARNING_F,
    alarm:
      typeof currentSensor?.alarmF === 'number' && Number.isFinite(currentSensor.alarmF)
        ? currentSensor.alarmF
        : FRIDGE_ALARM_F,
  };
  const fridgeThresholdMarkLines = [
    {
      yAxis: fridgeTempThresholds.warning,
      lineStyle: { color: 'orange', type: 'dashed', width: 2 },
      label: {
        formatter: `Warning ${fridgeTempThresholds.warning}°F`,
        position: 'insideEndTop',
        fontSize: 10,
        color: 'orange',
      },
    },
    {
      yAxis: fridgeTempThresholds.alarm,
      lineStyle: { color: 'firebrick', type: 'dashed', width: 2 },
      label: {
        formatter: `Alarm ${fridgeTempThresholds.alarm}°F`,
        position: 'insideEndTop',
        fontSize: 10,
        color: 'firebrick',
      },
    },
  ];

  const series = Array.isArray(Display) ? Display.map((display, index) => {
    const values = chartData.map((item) => (item as any)[display]);
    // Use blue for fridge/HVAC temperature lines so they don't clash with the
    // orange/red warning/alarm threshold lines.
    const color = isFridgeTemp
      ? (sensorColor || '#1E88E5')
      : colors[index % colors.length];

    // Special handling for RiverHeight
    if (display === 'Height') {
      return {
        name: 'River Height',
        type: 'line',
        data: values.map(value => ({
          value: value,
          itemStyle: {
            color: '#1E88E5'  // Keep the dots blue to match the line
          }
        })),
        lineStyle: {
          width: 2,
          color: '#1E88E5'  // A nice blue color for the line
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(30, 136, 229, 0.2)'  // Semi-transparent blue
              },
              {
                offset: 1,
                color: 'rgba(30, 136, 229, 0.2)'
              }
            ]
          }
        },
        smooth: true,
        tooltip: {
          valueFormatter: function (value: number | null | undefined) {
            if (value === null || value === undefined) {
              return 'No data';
            }
            return Number(value).toFixed(2);
          },
        },
        markLine: {
          symbol: 'none',
          data: [
            {
              yAxis: riverHeightThresholds.overDam,
              lineStyle: { 
                color: 'firebrick',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Dam',
                position: 'end',
                fontSize: 10,
                color: 'firebrick'
              }
            },
            {
              yAxis: riverHeightThresholds.overPathDam,
              lineStyle: { 
                color: 'orange',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Dam Path',
                position: 'end',
                fontSize: 10,
                color: 'orange'
              }
            },
            {
              yAxis: riverHeightThresholds.overPathDam2,
              lineStyle: { 
                color: 'gold',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Dam on 18',
                position: 'end',
                fontSize: 10,
                color: 'gold'
              }
            },
            {
              yAxis: riverHeightThresholds.overPath11,
              lineStyle: { 
                color: 'seagreen',
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Path at 11',
                position: 'end',
                fontSize: 10,
                color: 'seagreen'
              }
            },
            ...verticalLines
          ]
        }
      };
    }

    const fridgeMarks = isFridgeTemp && display === 'TempF' ? fridgeThresholdMarkLines : [];
    const verticals = index === 0 ? verticalLines : [];
    const markLineData = [...fridgeMarks, ...verticals];

    return {
      name: display,
      type: 'line',
      data: values.map(value => ({
        value: value,
        itemStyle: {
          color: color
        }
      })),
      lineStyle: {
        color: color,
      },
      itemStyle: {
        color: color,
      },
      smooth: true,
      tooltip: {
        valueFormatter: function (value: number | null | undefined) {
          if (value === null || value === undefined) {
            return 'No data';
          }
          return Number(value).toFixed(2);
        },
      },
      ...(markLineData.length > 0 ? {
        markLine: {
          symbol: 'none',
          data: markLineData,
        }
      } : {})
    };
  }) : [];

  const option = {
    tooltip: {
      show: !isMobile,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
      formatter: function(params: any) {
        const timestamp = moment.utc(chartData[params[0].dataIndex].timestamp).tz('America/New_York');
        const formattedTime = timestamp.format('MM/DD/YYYY h:mm:ss A');
        let result = `${formattedTime}<br/>`;
        params.forEach((param: any) => {
          const value = param.value === null || param.value === undefined ? 'No data' : Number(param.value).toFixed(2);
          const color = param.color;
          result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${param.seriesName}: ${value}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [
        ...series.map(s => s.name),
        ...(Array.isArray(Display) && Display.includes('Height') ? [
        ] : [])
      ],
      textStyle: {
        color: 'grey',
      },
      bottom: 0,
      padding: [5, 0, 0, 0],
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    toolbox: {
      feature: {
        saveAsImage: {},
      },
    },
    xAxis: {
      type: 'category',
      data: timestamps,
      axisLabel: {
        color: 'text.secondary',
        rotate: 45,
        interval: (index: number) => shouldShowLabel(index),
        formatter: (_value: string, index: number) => formatLabel(index),
      },
      name: 'Time',
      nameTextStyle: {
        color: 'grey',
      },
    },
    yAxis: {
      type: 'value',
      name: 'Value',
      axisLabel: {
        formatter: '{value}',
        color: 'text.secondary',
      },
      nameTextStyle: {
        color: 'grey',
      },
      ...(Array.isArray(Display) && Display.includes('Height') ? {
        min: Math.max(200, minRiverHeight - 5),
        max: 220
      } : {})
    },
    series: series,
  };

  useEffect(() => {
    const chartElement = document.querySelector('.echarts-for-react');
    if (chartElement) {
      const echartsInstance = (chartElement as any).__ECharts__;
      if (echartsInstance) {
        echartsInstance.clear();
        echartsInstance.setOption(option);
      }
    }
  }, [Display, chartData]);

  return (
    <ReactECharts
      key={`${deviceEUI}-${Array.isArray(Display) ? Display.join('-') : Display}`}
      option={option}
      className="cancelSelectorName"
      style={{ height, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default SensorGraph;
